import React, {
  CSSProperties,
  FC,
  PropsWithChildren,
  useCallback,
  useState,
} from "react";
import styled, { useTheme } from "styled-components";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import Card from "common/Boxes/Card";
import CaretRightOutlined from "@ant-design/icons/CaretRightOutlined";
import { ChevronRight } from "lucide-react";
import { useRefValue } from "@shared/utils/hooks/misc.ts";

interface ExpandingCardProps {
  blockClick?: boolean;
  collapsibleContent?: React.JSX.Element;
  expanded?: boolean;
  defaultExpanded?: boolean;
  id?: string;
  style?: CSSProperties;
  className?: string;
  setExpanded?: (expanded: boolean) => void;
  showArrow?: boolean;
  arrowIcon?: React.JSX.Element;
  onLongPress?: () => void;
}

const ExpandingCard: FC<PropsWithChildren<ExpandingCardProps>> = ({
  children,
  blockClick,
  collapsibleContent,
  expanded,
  setExpanded,
  defaultExpanded,
  id,
  style,
  className,
  showArrow,
  arrowIcon,
  onLongPress,
}) => {
  const { isNewLook } = useTheme();
  const [_expanded, _setExpanded] = useState(!!defaultExpanded);
  const localIsExpanded = expanded ?? _expanded;
  const localSetExpandedRef = useRefValue(setExpanded ?? _setExpanded);

  const toggleExpanded = useCallback(() => {
    localSetExpandedRef.current(!localIsExpanded);
  }, [localIsExpanded]);

  return (
    <Wrapper
      id={id}
      style={style}
      className={className}
      onClick={blockClick ? undefined : toggleExpanded}
      onLongPress={onLongPress}
    >
      <div style={{ position: "relative" }}>
        {children}
        {showArrow &&
          (arrowIcon ??
            (isNewLook ? (
              <NewLookArrow $expanded={expanded}>
                <ChevronRight size={16} strokeWidth={1.75} />
              </NewLookArrow>
            ) : (
              <Arrow $expanded={expanded} />
            )))}
      </div>
      {collapsibleContent && (
        <ExpandCollapse
          expanded={localIsExpanded}
          style={EXPAND_COLLAPSE_STYLE}
          animateAuto={false}
        >
          <ExpandingContentWrapper>
            {collapsibleContent}
          </ExpandingContentWrapper>
        </ExpandCollapse>
      )}
    </Wrapper>
  );
};

export default ExpandingCard;

const EXPAND_COLLAPSE_STYLE = { display: "grid" };

const Wrapper = styled(Card)`
  position: relative;
`;
const ExpandingContentWrapper = styled.div`
  margin-top: 16px;
  padding-top: 12px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    margin-top: 12px;
  `}
  border-top: ${(p) => p.theme.borders.lightest};
`;
const Arrow = styled(CaretRightOutlined)<{ $expanded?: boolean }>`
  position: absolute;
  color: ${(p) => p.theme.colors.mediumGrey};
  top: 50%;
  transform: translateY(-50%) rotate(${(p) => (p.$expanded ? 90 : 0)}deg);
  right: 0;
  transition: transform 0.35s;
`;
const NewLookArrow = styled.span<{ $expanded?: boolean }>`
  position: absolute;
  top: 50%;
  right: 0;
  display: inline-flex;
  color: ${(p) => p.theme.colors.lightestText};
  transform: translateY(-50%) rotate(${(p) => (p.$expanded ? 90 : 0)}deg);
  transition: transform 0.25s;
`;
